// The main table, split into the empties and everything else (Alan, 2026-09-21:
// "在主表格页面可以分屏...在表格分出额外的空柜的部分然后就变成了一半空柜一半其他的").
//
// The 配空 flow already splits the page into two tables while a pairing is chosen.
// This is the same machinery as a way of LOOKING at the fleet rather than a step in
// a flow: press 分屏 and the table becomes two - 其他 on the left, 空柜 on the right,
// the same way the pairing split sits - each a full table with the same editing,
// and press it again for the one table back. Below 900px the CSS stacks them.
//
// Since 2026-09-26 the two panes answer two questions. The toolbar - search, the
// chips, Filters, the date range - narrows the left pane and nothing else; the
// right pane has a search and a Filters of its own (Alan: "我想要分屏的两个部分分开
// ...在不同的split可以不同的筛选", and "这个空柜的页面是和另外的页面是分开独立的就是不要
// 让别的页面的筛选影响到空柜部分"). Before that the split was one partition of the
// toolbar's rows, so typing a number into the toolbar search emptied the empties.
//
// The right pane reads the Dashboard's own rows while neither side narrows
// anything - they already hold every empty, so the common case asks the server for
// nothing extra - and its own query from the first filter on (see
// ensureEmptiesSource in app.js).
//
// Everything that can be decided without a page is decided here, as plain
// functions, so it can be tested: what goes in which pane, when the split may be
// open at all, and how it opens and closes without leaving anything behind.

import { appointmentDaysMarkup, laDayKey, rowsByDay } from './dashboard-appointment-filter.mjs?v=20260806.100';

export const BROWSE_SPLIT_LAYOUTS = Object.freeze(['stack', 'side']);

/**
 * The date each pane is ordered and narrowed by (Alan, 2026-09-26: "重柜可以根据
 * pickup appt date 筛选，空柜按照 actual return ... 分着屏幕两边排序"), soonest
 * first, with a strip of days over each pane.
 */
export const BROWSE_SPLIT_DATE_FIELDS = Object.freeze({
  others: 'pickup_appointment_at',
  empty: 'actual_empty_returned_at',
});

/**
 * A row for the right-hand pane: reported empty, and not yet closed.
 *
 * Until 2026-09-26 this was the Empty chip's own rule - reported empty AND no
 * Actual Return - so that pressing the chip and reading the pane showed the same
 * Containers. Ordering the pane by Actual Return made that rule defeat itself:
 * the one date the pane is sorted by would move a Container OUT of the pane the
 * moment anyone entered it, and every row left behind would be undated. So the
 * pane keeps an empty through its Actual Return, until the Empty Return review
 * closes it - the pane is a schedule, the chip is still the pairing pool.
 */
export const BROWSE_SPLIT_EMPTY_STATUSES = Object.freeze(['empty_return_reported']);

export function browseSplitIsEmpty(row) {
  return BROWSE_SPLIT_EMPTY_STATUSES.includes(row?.ocean_status);
}

// Every accepted write bumps information_revision, so the higher one is the newer
// copy. A row without one is older than any row with one.
export function rowRevision(row) {
  const value = row?.information_revision;
  return Number.isSafeInteger(value) && value >= 0 ? value : -1;
}

/**
 * The newest copy of one Container among several lists of rows, or null.
 *
 * Two sources can each hold the same Container - the toolbar's rows and the empties
 * pane's own answer - and one of them may not have caught up. A save must name the
 * revision the server has now, and a pane must show what is true now, so both read
 * this. On a tie the first list wins.
 */
export function freshestRow(id, ...sources) {
  if (typeof id !== 'string' || !id) return null;
  let best = null;
  for (const rows of sources) {
    for (const row of Array.isArray(rows) ? rows : []) {
      if (row?.id === id && (best === null || rowRevision(row) > rowRevision(best))) best = row;
    }
  }
  return best;
}

export function normalizeBrowseSplitLayout(value) {
  return BROWSE_SPLIT_LAYOUTS.includes(value) ? value : 'side';
}

/**
 * Every row lands in exactly one pane.
 *
 * The empties are browseSplitIsEmpty's rows. Everything
 * else is the complement, not a second predicate, which is the whole guarantee:
 * a row cannot be listed twice and cannot be dropped, whatever status it holds.
 *
 * Alan has been burned by the first half of that. The pairing split once listed
 * about forty Containers in both panes because each pane had its own test and a
 * reported empty happened to pass both (2026-08-16).
 */
export function partitionRowsForBrowseSplit(rows) {
  const others = [];
  const empty = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || typeof row !== 'object') continue;
    (browseSplitIsEmpty(row) ? empty : others).push(row);
  }
  return { others, empty };
}

const rowKey = (row) => (typeof row.id === 'string' && row.id ? row.id : row);

/**
 * The two panes when each has a source of its own.
 *
 * `rows` is the toolbar's answer and feeds the left pane. `emptyRows` is the right
 * pane's own answer, which the toolbar never touches; null means it has none and the
 * right pane is drawn from `rows` exactly as before. `adoptEmpties` says the right
 * pane narrows nothing of its own, so an empty the toolbar's rows know about and its
 * own answer does not yet (somebody reported it a minute ago) belongs on it too.
 *
 * A Container in both lists is placed by its newer copy and shown as that copy, so
 * it is listed once and never dropped because one source is behind - the guarantee
 * the single partition gave, kept across two sources. A row in the right pane's
 * source that is no longer empty was edited out of it by hand; it goes left rather
 * than vanishing.
 */
export function browseSplitPanes({ rows = [], emptyRows = null, adoptEmpties = true } = {}) {
  const main = (Array.isArray(rows) ? rows : []).filter((row) => row && typeof row === 'object');
  if (!Array.isArray(emptyRows)) return partitionRowsForBrowseSplit(main);
  const own = emptyRows.filter((row) => row && typeof row === 'object');
  const newest = new Map();
  for (const row of [...main, ...own]) {
    const key = rowKey(row);
    const held = newest.get(key);
    if (!held || rowRevision(row) > rowRevision(held)) newest.set(key, row);
  }
  const placed = new Set();
  const collect = (candidates, wantEmpty) => {
    const pane = [];
    for (const row of candidates) {
      const key = rowKey(row);
      const copy = newest.get(key);
      if (placed.has(key) || browseSplitIsEmpty(copy) !== wantEmpty) continue;
      placed.add(key);
      pane.push(copy);
    }
    return pane;
  };
  const others = collect([...main, ...own], false);
  const empty = collect(adoptEmpties ? [...own, ...main] : own, true);
  return { others, empty };
}

/**
 * Whether the split may be on screen right now.
 *
 * It is a view preference, so a flow that needs the page - the 配空 pairing and
 * the three single-leg dispatches, all of which narrow and read the main table -
 * takes precedence and the split returns when the flow ends. `wide` is false on a
 * phone, where two tables would each be a sliver.
 */
export function browseSplitShouldBeOpen({ preferred = false, flowBusy = false, wide = true } = {}) {
  return preferred === true && flowBusy !== true && wide !== false;
}

const PANE_TITLES = Object.freeze({ others: '其他 Others', empty: '空柜 Empty' });

// Both panes carry a toolbar (Alan, 2026-09-27: "分屏后是完全的两套一样的操作系统").
// The left one is the Dashboard's own, moved in; the right one is its twin.
export const BROWSE_SPLIT_PANES = Object.freeze(['others', 'empty']);

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

export function browseSplitMarkup(layout = 'side', { tools = [] } = {}) {
  // The classes are the Dashboard toolbar's own, so a control moved in here, or drawn
  // here as a twin, is styled exactly as it is above the single table.
  const toolsFor = (key) => (tools.includes(key)
    ? `<div class="container-operations-controls pairing-split__toolbar"><div class="container-operations-command-row" data-pane-toolbar="${key}"></div></div>`
    : '');
  const pane = (key) => `<section class="pairing-split__pane" data-browse-pane="${key}">
        <div class="pairing-split__head">
          <h2 class="pairing-split__title"><span>${escapeHtml(PANE_TITLES[key])}</span><small data-pane-count></small></h2>
          ${toolsFor(key)}
        </div>
        <div class="pairing-split__days" data-pane-days="${key}" hidden></div>
        <div class="pairing-split__table" data-pane-host="${key}"></div>
      </section>`;
  return `<div class="pairing-split is-browse" data-browse-split data-layout="${escapeHtml(normalizeBrowseSplitLayout(layout))}">
      ${pane('others')}
      ${pane('empty')}
    </div>`;
}

/**
 * The count beside a pane's title. A trailing ellipsis while more pages are still
 * arriving: the fleet loads in the background after the first page, and a number
 * that is still climbing should not read as final.
 */
export function browseSplitCountText(count, loading) {
  return `${Number.isFinite(count) ? count : 0}${loading ? '…' : ''}`;
}

/**
 * The split's lifecycle. Owns one node in the page and two table controllers, and
 * nothing else - the caller decides WHEN, this decides how to leave no trace.
 *
 * `mountPane({ host, rows, paneKey })` is the same factory the pairing split uses,
 * so a pane here is exactly a Container table: same editors, same saving, same
 * modals. It is handed in rather than imported because the options it closes over
 * live in app.js.
 */
export function createBrowseSplit({
  documentRef,
  workspaceHost,
  mountPane,
  getRows,
  isLoading = () => false,
  isUnlocked = () => false,
  emptyReasonFor = () => '',
  onPaneMounted = () => {},
  layout = 'side',
  locale = () => 'zh',
  // The right pane's own rows, or null while it reads the toolbar's (see
  // browseSplitPanes), and whether it narrows nothing of its own.
  getEmptyRows = () => null,
  adoptEmpties = () => true,
  // Handed each pane's toolbar slot and table each time the split is drawn; what it
  // returns is called when the split goes away.
  onPaneTools = null,
  // Told the rows each pane holds after every repartition, for whatever is counted
  // over them (the chips).
  onRefreshed = () => {},
} = {}) {
  if (!documentRef || typeof documentRef.createElement !== 'function'
    || typeof documentRef.querySelectorAll !== 'function'
    || typeof workspaceHost !== 'function' || typeof mountPane !== 'function'
    || typeof getRows !== 'function') {
    throw new TypeError('Browse split options are invalid.');
  }
  let panes = null;
  let node = null;
  let currentLayout = normalizeBrowseSplitLayout(layout);
  // The day each pane is narrowed to, 'all' for none. Kept across refreshes, and
  // let go when the day has no rows left in it.
  const selectedDay = { others: 'all', empty: 'all' };
  let lastParts = { others: [], empty: [] };
  let releaseTools = [];
  const currentParts = () => browseSplitPanes({
    rows: getRows(),
    emptyRows: getEmptyRows(),
    adoptEmpties: adoptEmpties() !== false,
  });

  // Every split in the page, not the one this closure remembers. The node is a
  // SIBLING of the workspace, so it outlives a dashboard re-mount that replaces
  // the workspace while a closure variable starts out null in the new mount. The
  // pairing split shipped exactly that bug and Alan ended up with three stacked
  // copies (2026-08-19: "为什么突然出现这么多界面"). The DOM is what has to end up
  // clean, so removal is keyed off the DOM.
  const removeNodes = () => {
    let removed = 0;
    for (const stale of documentRef.querySelectorAll('[data-browse-split]')) {
      stale.remove();
      removed += 1;
    }
    node = null;
    return removed;
  };

  const syncLock = () => {
    const locked = !isUnlocked();
    panes?.others?.setLocked?.(locked);
    panes?.empty?.setLocked?.(locked);
  };

  // Narrow a pane to the selected day, or give it all its rows back.
  const applyDay = (key, rows) => {
    const field = BROWSE_SPLIT_DATE_FIELDS[key];
    const days = rowsByDay(rows, field);
    if (selectedDay[key] !== 'all' && !days[selectedDay[key]]) selectedDay[key] = 'all';
    const pane = panes?.[key];
    if (!pane?.setRowIdFilter) return days;
    pane.setRowIdFilter(selectedDay[key] === 'all'
      ? null
      : rows.filter((row) => laDayKey(row?.[field]) === selectedDay[key]).map((row) => row.id));
    return days;
  };

  const paint = ({ others, empty }) => {
    if (!node) return;
    // Each pane is still arriving on its own schedule now.
    const counts = node.querySelectorAll('[data-pane-count]');
    if (counts[0]) counts[0].textContent = browseSplitCountText(others.length, Boolean(isLoading('others')));
    if (counts[1]) counts[1].textContent = browseSplitCountText(empty.length, Boolean(isLoading('empty')));
    for (const [key, rows] of [['others', others], ['empty', empty]]) {
      const strip = node.querySelector(`[data-pane-days="${key}"]`);
      if (!strip) continue;
      const days = rowsByDay(rows, BROWSE_SPLIT_DATE_FIELDS[key]);
      // No dated row, no strip: a lone 全部 would be a button that does nothing.
      strip.hidden = Object.keys(days).length === 0;
      strip.innerHTML = strip.hidden ? '' : appointmentDaysMarkup({
        days, total: rows.length, selected: selectedDay[key], locale: locale(),
      });
    }
  };

  const onDayClick = (event) => {
    const button = event.target?.closest?.('[data-appointment-day]');
    const key = button?.closest?.('[data-pane-days]')?.dataset?.paneDays;
    if (!button || !key || !panes) return;
    selectedDay[key] = button.dataset.appointmentDay;
    applyDay(key, lastParts[key]);
    paint(lastParts);
  };

  function refresh() {
    if (!panes) return false;
    const parts = currentParts();
    lastParts = parts;
    panes.others.setRows(parts.others);
    panes.empty.setRows(parts.empty);
    applyDay('others', parts.others);
    applyDay('empty', parts.empty);
    panes.others.setEmptyReason?.(emptyReasonFor('others'));
    panes.empty.setEmptyReason?.(emptyReasonFor('empty'));
    paint(parts);
    syncLock();
    onRefreshed(parts);
    return true;
  }

  function open() {
    // Idempotent: a second open is a refresh, never a second split.
    if (panes) return refresh();
    const workspace = workspaceHost();
    // Loud. The pairing split once returned silently here for a day and its button
    // looked dead - a missing dependency must never read as "nothing to do".
    if (!workspace) throw new Error('the Container workspace is not on the page');
    removeNodes();
    const holder = documentRef.createElement('div');
    holder.innerHTML = browseSplitMarkup(currentLayout, {
      tools: typeof onPaneTools === 'function' ? BROWSE_SPLIT_PANES : [],
    });
    node = holder.firstElementChild ?? holder.children?.[0] ?? null;
    if (!node) throw new Error('the split could not be drawn');
    workspace.parentNode?.insertBefore(node, workspace.nextSibling);
    workspace.hidden = true;
    const parts = currentParts();
    lastParts = parts;
    panes = {
      others: mountPane({ host: node.querySelector('[data-pane-host="others"]'), rows: parts.others, paneKey: 'others' }),
      empty: mountPane({ host: node.querySelector('[data-pane-host="empty"]'), rows: parts.empty, paneKey: 'empty' }),
    };
    onPaneMounted('others', panes.others);
    onPaneMounted('empty', panes.empty);
    for (const key of ['others', 'empty']) {
      panes[key].setDateSort?.(BROWSE_SPLIT_DATE_FIELDS[key], { ascending: true });
      applyDay(key, parts[key]);
    }
    node.addEventListener?.('click', onDayClick);
    if (typeof onPaneTools === 'function') {
      for (const key of BROWSE_SPLIT_PANES) {
        const released = onPaneTools({
          key,
          toolbar: node.querySelector(`[data-pane-toolbar="${key}"]`),
          pane: panes[key],
        });
        if (typeof released === 'function') releaseTools.push(released);
      }
    }
    panes.others.setEmptyReason?.(emptyReasonFor('others'));
    panes.empty.setEmptyReason?.(emptyReasonFor('empty'));
    paint(parts);
    syncLock();
    onRefreshed(parts);
    return true;
  }

  function close() {
    const hadPanes = panes !== null;
    // Before the node goes: a toolbar moved in has to be carried back out of it.
    for (const release of releaseTools.splice(0)) release();
    panes?.others?.destroy?.();
    panes?.empty?.destroy?.();
    panes = null;
    selectedDay.others = 'all';
    selectedDay.empty = 'all';
    const removed = removeNodes();
    // Only give the workspace back if it was this split that took it. A pairing
    // may have hidden it since, and unhiding it under the pairing's own split would
    // put two tables on screen at once.
    if (hadPanes || removed > 0) {
      const workspace = workspaceHost();
      if (workspace) workspace.hidden = false;
    }
    return hadPanes || removed > 0;
  }

  function setLayout(next) {
    currentLayout = normalizeBrowseSplitLayout(next);
    if (node?.setAttribute) node.setAttribute('data-layout', currentLayout);
    return currentLayout;
  }

  return Object.freeze({
    open,
    close,
    refresh,
    setLayout,
    syncLock,
    isOpen: () => panes !== null,
    layout: () => currentLayout,
    // What a pane is showing right now, before its day strip narrows it.
    paneRows: (key) => (panes ? [...(lastParts[key] ?? [])] : []),
    // A pane's table, for the toolbar above it (copy and paste work in its order).
    paneTable: (key) => panes?.[key] ?? null,
    // The pane controllers, for whatever the caller keeps in step with the page
    // (the dispatch highlight lives inside each table instance).
    forEachPane(callback) {
      if (!panes) return;
      callback('others', panes.others);
      callback('empty', panes.empty);
    },
    destroy() { close(); },
  });
}
